import { useEffect, useMemo, useRef } from 'react';
import {
  BufferGeometry,
  DoubleSide,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Object3D,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { GroupProps } from '@react-three/fiber';
import { ProjectionGeneratorWorker, SilhouetteGeneratorWorker } from '@spazious/dollhouse-core';
import { isBufferGeometry, isMesh } from '@spazious/dollhouse-three';

export interface EdgeProjectionProps extends Omit<GroupProps, 'children'> {
  // children?: Object3DNode<Mesh, typeof Mesh>;
  object?: Object3D;
  silhouetteWorkerPath?: string;
  projectionWorkerPath?: string;
}

const ANGLE_THRESHOLD = 20;

const DEFAULT_SHILHOUETTE_WORKER_PATH = new URL(
  '@spazious/dollhouse-core/worker/silhouetteAsync.js',
  import.meta.url
).toString();

const DEFAULT_PROJECTION_WORKER_PATH = new URL(
  '@spazious/dollhouse-core/worker/projectionAsync.js',
  import.meta.url
).toString();

export const EdgeProjection: React.FC<EdgeProjectionProps> = ({
  object,
  silhouetteWorkerPath = DEFAULT_SHILHOUETTE_WORKER_PATH,
  projectionWorkerPath = DEFAULT_PROJECTION_WORKER_PATH,
}) => {
  const groupRef = useRef<Group>(null);

  const silhouette = useMemo(
    () =>
      new Mesh(
        new BufferGeometry(),
        new MeshBasicMaterial({
          color: '#eee',
          polygonOffset: true,
          polygonOffsetFactor: 3,
          polygonOffsetUnits: 3,
          side: DoubleSide,
        })
      ),
    []
  );

  const outlines = useMemo(
    () => new LineSegments(new BufferGeometry(), new LineBasicMaterial({ color: 0x030303 })),
    []
  );

  const mergedGeometry = useMemo(() => {
    if (object) {
      const geometries: BufferGeometry[] = [];
      object.updateWorldMatrix(true, true);
      object.traverse((c) => {
        if (isMesh(c) && isBufferGeometry(c.geometry)) {
          const clone = c.geometry.clone();
          clone.applyMatrix4(c.matrixWorld);
          for (const key in clone.attributes) {
            if (key !== 'position') {
              clone.deleteAttribute(key);
            }
          }

          geometries.push(clone);
        }
      });
      return mergeGeometries(geometries, false);
    }

    return new BufferGeometry();
  }, [object]);

  useEffect(() => {
    new SilhouetteGeneratorWorker(silhouetteWorkerPath)
      .generate(mergedGeometry, {
        sortTriangles: true,
      })
      .then((data) => {
        silhouette.geometry.dispose();
        silhouette.geometry = data as any;
        silhouette.visible = true;
      });
    new ProjectionGeneratorWorker(projectionWorkerPath)
      .generate(mergedGeometry, {
        angleThreshold: ANGLE_THRESHOLD,
      })
      .then((data) => {
        outlines.geometry.dispose();
        outlines.geometry = data as any;
        outlines.visible = true;
      });
  }, [mergedGeometry, outlines, projectionWorkerPath, silhouette, silhouetteWorkerPath]);

  return (
    <group ref={groupRef}>
      <primitive object={silhouette} />
      <primitive object={outlines} />
    </group>
  );
};

export default EdgeProjection;
